import { inject, signal, DestroyRef, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Api } from '@api/api';
import {
  createStoreReview,
  createVoteStoreReview,
  downvoteStoreReview,
  getStoreReviews,
  getStoreReviewsCount,
  getStoreReviewsAverageRating,
} from '@api/functions';
import type { AddStoreReviewDto, ReviewsSort } from '@api/models';
import { onWindowLoad } from '../helpers/on-window-load.function';

@Service()
export class StoreReviewService {
  private api = inject(Api);
  private destroyRef = inject(DestroyRef);

  readonly storeReviewsCount = signal<number | undefined>(undefined);
  readonly averageRating = signal<number | undefined>(undefined);

  constructor() {
    this.setReviewsCount();
    this.setAverageRating();

    onWindowLoad(() => this.setReviewsCount());
  }

  setReviewsCount() {
    this.api.invoke(getStoreReviewsCount)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => this.storeReviewsCount.set(response.data),
      });
  }

  fetchAllReviews(limit: number, page: number, sort?: ReviewsSort) {
    return this.api.invoke(getStoreReviews, { limit, page, sort });
  }

  addReview(reviewDto: AddStoreReviewDto) {
    return this.api.invoke(createStoreReview, { body: reviewDto });
  }

  vote(reviewId: number) {
    return this.api.invoke(createVoteStoreReview, { id: String(reviewId) });
  }

  downvote(reviewId: number) {
    return this.api.invoke(downvoteStoreReview, { id: String(reviewId) });
  }

  setAverageRating(): void {
    this.api.invoke(getStoreReviewsAverageRating)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => this.averageRating.set(response.data),
        error: err => {
          console.warn(`${new Date().toISOString()} - Could not set store reviews avg rating`, err.message);
        }
      });
  }
}
